import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Mail, Globe, Send, Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { WHATSAPP_DISPLAY, whatsappLink } from "@/data/catalog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !/^[0-9]{10}$/.test(form.phone.replace(/\s/g, "")) || !form.message.trim()) {
      return toast.error("Please fill your name, a valid 10-digit phone and your message");
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you! Our team will contact you soon.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Could not send. Please email support@11starwellness.com");
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="contact-page" className="py-14 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-2xl mb-12">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C05C46] mb-3">
            Get in Touch
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1c2826] mb-4">
            Contact Eleven Stars
          </h1>
          <p className="text-base text-[#4B5563] leading-relaxed">
            Questions about a product, your order or bulk purchase? Send us a message or use the
            live chat — we reply quickly.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10">
          <div className="space-y-4">
            {[
              {
                icon: MapPin,
                title: "Our Address",
                text: "F-13, First Floor, Royal Plaza, Central Spine, Vidyadhar Nagar, Jaipur, Rajasthan — 302039",
              },
              { icon: Phone, title: "WhatsApp", text: WHATSAPP_DISPLAY, href: whatsappLink("Namaste! I have a question about Eleven Stars products.") },
              { icon: Mail, title: "Email", text: "support@11starwellness.com", href: "mailto:support@11starwellness.com" },
              { icon: Globe, title: "Website", text: "www.11starwellness.com", href: "https://www.11starwellness.com" },
            ].map((c) => (
              <div key={c.title} className="flex gap-4 bg-white rounded-xl border border-[#E5E0D8] p-5 shadow-sm">
                <span className="w-11 h-11 rounded-full bg-[#7C9A82]/20 flex items-center justify-center shrink-0">
                  <c.icon className="w-5 h-5 text-[#1A4331]" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-semibold text-[#1c2826]">{c.title}</h3>
                  {c.href ? (
                    <a
                      href={c.href}
                      data-testid={`contact-${c.title.toLowerCase()}`}
                      className="text-sm text-[#C05C46] hover:underline"
                      {...(c.href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
                    >
                      {c.text}
                    </a>
                  ) : (
                    <p className="text-sm text-[#4B5563] leading-relaxed">{c.text}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-[#E5E0D8] shadow-sm p-6 sm:p-8">
            <h2 className="font-display text-2xl font-semibold text-[#1c2826] mb-6">Send an Enquiry</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="en-name">Name *</Label>
                <Input data-testid="enquiry-name" id="en-name" value={form.name} onChange={set("name")} className="bg-[#F9F6F0]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="en-phone">Phone *</Label>
                  <Input data-testid="enquiry-phone" id="en-phone" value={form.phone} onChange={set("phone")} className="bg-[#F9F6F0]" />
                </div>
                <div>
                  <Label htmlFor="en-email">Email</Label>
                  <Input data-testid="enquiry-email" id="en-email" type="email" value={form.email} onChange={set("email")} className="bg-[#F9F6F0]" />
                </div>
              </div>
              <div>
                <Label htmlFor="en-message">Message *</Label>
                <Textarea data-testid="enquiry-message" id="en-message" rows={4} value={form.message} onChange={set("message")} className="bg-[#F9F6F0]" />
              </div>
              <Button
                data-testid="enquiry-submit"
                onClick={submit}
                disabled={sending}
                className="w-full rounded-full bg-[#C05C46] hover:bg-[#a84d3a] text-white py-6 font-semibold"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Message
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
